import type { ExportingMimeTypeValue, ExportingOptions, Options } from 'highcharts';
import type Highcharts from 'highcharts/highstock';

import {
  ensureWatermarkJpgDataUris,
  getWatermarkConfig
} from '@/components/Chart/hooks/options/useChartFunctions';

declare module 'highcharts' {
  interface Chart {
    exportChart: (options: ExportingOptions, chartOptions?: Options) => void;
    getSVG: (chartOptions?: Options) => string;
  }
}

export type ExportWatermarkOptions = {
  watermarkName: string;
  useSmallWatermark: boolean;
};

type ExportChartImageParams = {
  chart: Highcharts.Chart;
  type: ExportingMimeTypeValue;
  filename: string;
  options: Highcharts.Options;
  exportingOptions?: Omit<Highcharts.ExportingOptions, 'type' | 'filename'>;
  watermarkOptions?: ExportWatermarkOptions;
};

type HighchartsExportingInstance = {
  getSVGForExport?: (
    exportingOptions?: Highcharts.ExportingOptions,
    chartOptions?: Highcharts.Options
  ) => Promise<string>;
  downloadSVG?: (svg: string, exportingOptions: Highcharts.ExportingOptions) => Promise<void>;
};

type JsPdfDocument = {
  addFileToVFS: (filename: string, filecontent: string) => void;
  addFont: (postScriptName: string, id: string, fontStyle: string) => void;
  setFont: (fontName: string) => void;
  addImage: (...args: unknown[]) => void;
  save: (filename: string) => void;
};

type JsPdfConstructor = {
  new (options?: Record<string, unknown>): JsPdfDocument;
  API: {
    events: Array<[string, (this: JsPdfDocument) => void]>;
  };
};

const parseSvgNumber = (value: string | null | undefined, fallback: number): number => {
  const parsed = Number.parseFloat(value ?? '');
  return Number.isFinite(parsed) ? parsed : fallback;
};

const getSvgSize = (svg: SVGSVGElement): { width: number; height: number } => {
  const width = parseSvgNumber(svg.getAttribute('width'), 600);
  const height = parseSvgNumber(svg.getAttribute('height'), 400);
  return { width, height };
};

const getPlotBox = (
  svg: SVGSVGElement
): { x: number; y: number; width: number; height: number } => {
  const { width: svgWidth, height: svgHeight } = getSvgSize(svg);
  const plotBackground = svg.querySelector('.highcharts-plot-background');
  if (!plotBackground) {
    return { x: 0, y: 0, width: svgWidth, height: svgHeight };
  }

  return {
    x: parseSvgNumber(plotBackground.getAttribute('x'), 0),
    y: parseSvgNumber(plotBackground.getAttribute('y'), 0),
    width: parseSvgNumber(plotBackground.getAttribute('width'), svgWidth),
    height: parseSvgNumber(plotBackground.getAttribute('height'), svgHeight)
  };
};

export const addWatermarkToSvg = (
  svg: string,
  watermarkOptions?: ExportWatermarkOptions
): string => {
  if (!watermarkOptions) return svg;

  const watermark = getWatermarkConfig(watermarkOptions.watermarkName);
  if (!watermark.svgContent) return svg;

  const parser = new DOMParser();
  const svgDocument = parser.parseFromString(svg, 'image/svg+xml');
  const root = svgDocument.documentElement;
  if (root.nodeName.toLowerCase() === 'parsererror') return svg;

  root.querySelectorAll('.highcharts-gx-export-watermark').forEach(node => node.remove());

  const watermarkRoot = parser.parseFromString(
    watermark.svgContent,
    'image/svg+xml'
  ).documentElement;
  if (watermarkRoot.nodeName.toLowerCase() === 'parsererror') return svg;

  const plotBox = getPlotBox(root as unknown as SVGSVGElement);
  const width = watermarkOptions.useSmallWatermark ? 100 : plotBox.width;
  const height = width / watermark.aspectRatio;
  const x = watermarkOptions.useSmallWatermark
    ? plotBox.x + plotBox.width - width
    : plotBox.x + (plotBox.width - width) / 2;
  const y = watermarkOptions.useSmallWatermark
    ? plotBox.y + plotBox.height - height
    : plotBox.y + (plotBox.height - height) / 2;
  const scaleX = width / watermark.viewBox.width;
  const scaleY = height / watermark.viewBox.height;

  const group = svgDocument.createElementNS('http://www.w3.org/2000/svg', 'g');
  group.setAttribute('class', 'highcharts-gx-export-watermark');
  group.setAttribute('data-z-index', '0');
  group.setAttribute('pointer-events', 'none');
  group.setAttribute(
    'transform',
    `translate(${x - watermark.viewBox.x * scaleX} ${y - watermark.viewBox.y * scaleY}) scale(${scaleX} ${scaleY})`
  );

  Array.from(watermarkRoot.childNodes).forEach(child => {
    group.appendChild(svgDocument.importNode(child, true));
  });

  const seriesGroup = root.querySelector('.highcharts-series-group');
  root.insertBefore(group, seriesGroup ?? null);

  return new XMLSerializer().serializeToString(svgDocument);
};

// Lazily pre-load jsPDF + svg2pdf so Highcharts offline-exporting skips the
// CDN fetch and Cairo is always registered as "HighchartsFont".
// Highcharts skips pdfFont loading when SVG text is ASCII-only, leaving
// text elements with font-family "HighchartsFont" that jsPDF cannot resolve
// unless we pre-register it here.
export const ensurePdfDeps = async (): Promise<void> => {
  if ((globalThis as Record<string, unknown>).jspdf) return;

  const [{ jsPDF }] = await Promise.all([import('jspdf'), import('svg2pdf.js')]);

  try {
    const res = await fetch('/fonts/Cairo.ttf');
    if (res.ok) {
      const blob = await res.blob();
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (typeof reader.result === 'string') {
            resolve(reader.result.split(',')[1]);
          } else {
            reject(new Error('font read failed'));
          }
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Push a single "initialized" handler — runs on every new jsPDF instance
      const JsPDF = jsPDF as JsPdfConstructor;
      JsPDF.API.events.push([
        'initialized',
        function (this: JsPdfDocument) {
          for (const style of ['normal', 'bold', 'italic', 'bolditalic']) {
            this.addFileToVFS(style, base64);
            this.addFont(style, 'HighchartsFont', style);
          }
          this.setFont('HighchartsFont');
        }
      ]);
    }
  } catch {
    // best-effort — PDF falls back to default fonts if Cairo is unavailable
  }

  (globalThis as Record<string, unknown>).jspdf = { jsPDF };
};

const svgToCanvas = (svg: string, width: number, height: number): Promise<HTMLCanvasElement> =>
  new Promise((resolve, reject) => {
    const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(url);
        reject(new Error('Canvas 2D context unavailable'));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      resolve(canvas);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('SVG render failed'));
    };
    img.src = url;
  });

// Highcharts' svg2pdf path silently drops chart content for non-stock chart
// instances. Bypass it by rendering the export SVG to a canvas (browser-native,
// which correctly handles all Highcharts series types) and embedding the
// resulting PNG in a jsPDF document.
export const exportChartAsPng = async (
  chart: Highcharts.Chart,
  chartOptions: Options,
  sourceWidth: number,
  filename: string,
  watermarkOptions?: ExportWatermarkOptions
): Promise<void> => {
  await ensurePdfDeps();
  await ensureWatermarkJpgDataUris();

  const rawSvg = chart.getSVG({
    ...chartOptions,
    chart: { ...chartOptions.chart, width: sourceWidth }
  });
  const watermarkedRawSvg = addWatermarkToSvg(rawSvg, watermarkOptions);

  // Replace Highcharts' internal font alias so the browser uses Cairo when
  // rendering the SVG via canvas.
  const svg = watermarkedRawSvg
    .replace(/font-family="HighchartsFont"/g, 'font-family="Cairo, sans-serif"')
    .replace(/font-family:\s*['"]?HighchartsFont['"]?/g, 'font-family: Cairo, sans-serif');

  const svgEl = new DOMParser().parseFromString(svg, 'image/svg+xml').documentElement;
  const pdfW = Number.parseFloat(svgEl.getAttribute('width') ?? String(sourceWidth));
  const pdfH = Number.parseFloat(svgEl.getAttribute('height') ?? String(chart.chartHeight));

  const RASTER_SCALE = 2;
  const canvas = await svgToCanvas(
    svg,
    Math.round(pdfW) * RASTER_SCALE,
    Math.round(pdfH) * RASTER_SCALE
  );
  const pngData = canvas.toDataURL('image/png');

  const { jsPDF } = (globalThis as Record<string, unknown>).jspdf as {
    jsPDF: JsPdfConstructor;
  };
  const pdf = new jsPDF({
    orientation: pdfW >= pdfH ? 'l' : 'p',
    unit: 'px',
    format: [pdfW, pdfH],
    hotfixes: ['px_scaling']
  });
  pdf.addImage(pngData, 'PNG', 0, 0, pdfW, pdfH);
  pdf.save(`${filename}.pdf`);
};

export const exportChartImage = async ({
  chart,
  type,
  filename,
  options,
  exportingOptions,
  watermarkOptions
}: ExportChartImageParams): Promise<void> => {
  if (type === 'application/pdf') {
    await ensurePdfDeps();
  }
  if (type !== 'image/svg+xml') {
    await ensureWatermarkJpgDataUris();
  }
  const exportOptions = { type, filename, ...exportingOptions };
  const exporting = (chart as Highcharts.Chart & { exporting?: HighchartsExportingInstance })
    .exporting;

  if (!exporting?.getSVGForExport || !exporting.downloadSVG) {
    chart.exportChart(exportOptions, options);
    return;
  }

  const svg = await exporting.getSVGForExport(exportOptions, options);
  const watermarkedSvg = addWatermarkToSvg(svg, watermarkOptions);
  await exporting.downloadSVG(watermarkedSvg, exportOptions);
};
